import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function TasksPage() {
  const { activeProjectId } = useProjects();
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/tasks/${activeProjectId}`);
    setRows(data);
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    setRows((r) => r.map((x) => x.id === id ? { ...x, status } : x));
  };

  const create = async () => {
    if (!title.trim()) return;
    await api.post("/tasks", { project_id: activeProjectId, title, priority: "medium" });
    toast.success("Task created");
    setTitle("");
    load();
  };

  return (
    <DashboardLayout>
      <PageHeader overline="Tasks" title="Pipeline of pending work." description="Anything created from audits, content reviews, or growth-plan steps lands here." />

      <div className="bg-white border border-border rounded-sm p-4 mb-5 flex gap-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New task title" data-testid="task-title-input" className="bg-white border-border rounded-sm" />
        <button onClick={create} data-testid="task-create-btn" className="px-5 py-2 bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm text-sm">Add</button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-3">Task</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-border" data-testid={`task-row-${t.id}`}>
                <td className="px-4 py-3 font-medium">{t.title}</td>
                <td className="px-4 py-3 text-muted-foreground capitalize">{t.source}</td>
                <td className="px-4 py-3 capitalize">{t.priority}</td>
                <td className="px-4 py-3">
                  <Select value={t.status} onValueChange={(v) => update(t.id, v)}>
                    <SelectTrigger className="w-36 h-8 rounded-sm bg-white" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["open", "in_progress", "closed"].map((s) => <SelectItem key={s} value={s}><StatusBadge status={s} /></SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
